import { useRef, useState } from 'react';
import { Link } from 'react-router';
import clsx from 'clsx';
import {
  agentPathProblem,
  fixedFileNames,
  formatFileSize,
  frontEndSteps,
  isFilePart,
  isTemplatedRef,
  replaceValue,
  scriptFileUses,
  scriptVariables,
  VARIABLE_KEY_RE,
  type Check,
  type Extractor,
  type FileSource,
  type HttpStep,
  type MultipartPart,
  type PerfFileView,
  type PerfScript,
  type Step,
  type TextPlace,
} from '@1qa/shared';
import { ArrowDown, ArrowUp, Ban, ChevronDown, ChevronRight, CircleAlert, KeyRound, Link2, Paperclip, Plus, TextSelect, Timer, Trash2, Wand2 } from '@/components/icons';
import { usePerfFiles } from '@/lib/perfFiles';
import { FileThumb } from './TestFiles';
import { Badge, Button, Field, Input, Select, Tabs, TabPanel, Textarea, Toggle, type Tone } from '@/components/ui';

/** What the editor knows about the project's test data: key -> secret. */
export type TestDataKeys = Map<string, boolean>;

/** A value made test data from a selection: the parent saves it to the project when asked. */
export interface NewTestData {
  key: string;
  value: string;
  isSecret: boolean;
}

const VAR_RE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
const METHOD_TONE: Record<string, Tone> = { GET: 'info', POST: 'success', PUT: 'warning', PATCH: 'warning', DELETE: 'danger' };
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const FROM_LABEL: Record<Extractor['from'], { label: string; placeholder: string; hint: string }> = {
  json: { label: 'JSON response', placeholder: '$.data.token', hint: 'Path in the JSON: $.items[0].id, $.items[?(@.sku==\'B2\')].id' },
  header: { label: 'Response header', placeholder: 'x-request-token', hint: 'The header’s name' },
  cookie: { label: 'Cookie', placeholder: 'session', hint: 'The cookie’s name' },
  html: { label: 'HTML page', placeholder: 'input[name="csrf"]@value', hint: 'CSS selector, then @ and the attribute (@text for the text)' },
  regex: { label: 'Text pattern', placeholder: 'token=([a-z0-9]+)', hint: 'A regular expression with one ( ) group: the value' },
};
const GENERATORS = ['$uuid', '$timestamp_ms', '$timestamp_s', '$iso_now', '$random_int(1,1000)', '$random_alnum(12)'];
/** Templates are written with these suffixes so the value is encoded again for where it goes, every time it runs. */
const SUFFIX: Record<TextPlace, string> = { url: ':url', form: ':form', json: ':json', header: '', text: '' };

/** The recorded value a selection stands for, decoded the way its place holds it. */
function decodeFor(raw: string, place: TextPlace): string {
  try {
    if (place === 'url') return decodeURIComponent(raw);
    if (place === 'form') return decodeURIComponent(raw.replace(/\+/g, ' '));
    if (place === 'json') return JSON.parse(`"${raw}"`) as string;
  } catch {
    /* not encoded after all */
  }
  return raw;
}

/** A name for test data from the text before the selection: `email=` / `"email":"` / `email: ` -> EMAIL. */
function suggestKey(before: string): string {
  const m = /([A-Za-z][A-Za-z0-9_-]{0,40})["']?\s*[:=]\s*["']?$/.exec(before);
  const name = (m?.[1] ?? 'value').replace(/[^A-Za-z0-9]+/g, '_').replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
  return /^[A-Z]/.test(name) ? name.slice(0, 64) : `VALUE_${name}`.slice(0, 64);
}

/** Renames `{{old}}` (with any encoding / written value) to `{{name}}` in the whole script. */
function renameTemplate(script: PerfScript, old: string, name: string): PerfScript {
  if (!old || old === name) return script;
  const re = new RegExp(`\\{\\{(\\s*)${old.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s*(?::(?:url|form|json))?\\s*(?:\\|[^{}]*)?\\}\\})`, 'g');
  return JSON.parse(JSON.stringify(script).replace(re, `{{$1${name}`)) as PerfScript;
}

/** A switch with its text beside it (Toggle's label is for screen readers only). */
function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <Toggle checked={checked} onChange={onChange} label={label} disabled={disabled} />
      <span className="text-sm text-fg-secondary" aria-hidden>
        {label}
      </span>
    </div>
  );
}

/** A text with its {{templates}} highlighted. */
function TemplateText({ text, className }: { text: string; className?: string }) {
  return (
    <span className={clsx('font-mono break-all', className)}>
      {text.split(/(\{\{[^{}]*\}\})/).map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="rounded bg-primary-subtle px-0.5 text-primary">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </span>
  );
}

interface FieldContext {
  readOnly: boolean;
  /** Names a template may use at this step: values taken by earlier responses, test data, iteration values. */
  insertable: { group: string; names: string[] }[];
  /** Turns the selected text into test data: here only, or everywhere it appears. */
  parameterize: (p: { raw: string; value: string; place: TextPlace; before: string; replaceHere: (template: string) => void }) => void;
  /** The project's test files, for upload fields. */
  files: PerfFileView[];
}

/** The picker's choice that switches a file field to a name from test data (a data file column, one file per journey). */
const PER_JOURNEY = '__per_journey__';

/**
 * Which file a field sends. Uploaded to 1QA: one of the project's test files, or {{NAME}} so each journey takes the file
 * its data file row (or the test data) names; a name not uploaded yet (a recorded upload) is pointed out. On the load
 * agent: a path inside the folder the agent was started with (--files-dir), templates allowed (photos/{{IMAGE}}).
 */
function FilePicker({ label, value, source, onChange, ctx }: { label: string; value: string; source: FileSource; onChange: (v: string, source: FileSource) => void; ctx: FieldContext }) {
  const templated = isTemplatedRef(value);
  const known = source === 'project' ? ctx.files.find((f) => f.name.toLowerCase() === value.trim().toLowerCase()) : undefined;
  const options = [
    { value: '', label: 'Choose a test file…' },
    ...ctx.files.map((f) => ({ value: f.name, label: `${f.name} (${formatFileSize(f.sizeBytes)})` })),
    // A recorded name that is not uploaded yet stays choosable, so the field shows what it sends.
    ...(value && !templated && !known ? [{ value, label: `${value} (not uploaded yet)` }] : []),
    { value: PER_JOURNEY, label: 'A different file per journey ({{NAME}})…' },
  ];
  // Only the written parts can be checked here; values from test data are checked when the run starts.
  const pathProblem = source === 'agent' && value.trim() ? agentPathProblem(value.replace(/\{\{[^{}]*\}\}/g, 'x')) : null;
  return (
    <div className="space-y-2">
      <Field label="Where the file is">
        {(id) => (
          <Select
            id={id}
            disabled={ctx.readOnly}
            wrapperClassName="max-w-sm"
            value={source}
            onChange={(e) => onChange(e.target.value === 'agent' && !templated && known ? '' : value, e.target.value as FileSource)}
            options={[
              { value: 'project', label: 'Uploaded to 1QA (Test files)' },
              { value: 'agent', label: 'On the load agent (its files folder)' },
            ]}
          />
        )}
      </Field>
      {source === 'agent' ? (
        <>
          <TemplateField
            label={`${label}: path in the agent’s files folder`}
            place="text"
            value={value}
            ctx={ctx}
            hint="Relative to the folder the agent was started with (--files-dir), like photos/0001.jpg. Write photos/{{IMAGE}} and list a file name per row in a data file, so each journey sends a different file. Every agent of the run needs the files."
            onChange={(v) => onChange(v, 'agent')}
          />
          {pathProblem && <p className="text-xs text-warning">This path can’t be used: {pathProblem}.</p>}
        </>
      ) : (
        <>
          <Field label={label}>
            {(id) => (
              <div className="flex items-center gap-3">
                {known && <FileThumb file={known} className="size-9" />}
                <Select
                  id={id}
                  disabled={ctx.readOnly}
                  wrapperClassName="min-w-0 flex-1"
                  value={templated ? PER_JOURNEY : value}
                  onChange={(e) => onChange(e.target.value === PER_JOURNEY ? (templated ? value : '{{IMAGE}}') : e.target.value, 'project')}
                  options={options}
                />
              </div>
            )}
          </Field>
          {templated && (
            <TemplateField
              label="File name from test data"
              place="text"
              value={value}
              ctx={ctx}
              hint="Each journey sends the test file this names: list one file name per row in a data file column (IMAGE: cat.png, dog.png…), or set it in the test data."
              onChange={(v) => onChange(v, 'project')}
            />
          )}
          {value.trim() && !templated && !known && (
            <p className="text-xs text-warning">
              {value} is not one of this project’s test files yet. Browsers don’t record the files themselves: upload it under Scripts › Test files, or choose another file.
            </p>
          )}
        </>
      )}
      {!value.trim() && <p className="text-xs text-warning">Choose the file this field sends: the recording names no file, or it was cleared.</p>}
    </div>
  );
}

/** The fields of an upload form, in order: text fields (with templates) and file fields. */
function MultipartFields({ parts, onChange, ctx }: { parts: MultipartPart[]; onChange: (p: MultipartPart[]) => void; ctx: FieldContext }) {
  const set = (i: number, part: MultipartPart) => onChange(parts.map((x, k) => (k === i ? part : x)));
  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-secondary">The fields are sent in this order; 1QA writes the boundary and the Content-Type header for each request.</p>
      <ol className="space-y-3">
        {parts.map((part, i) => (
          <li key={i} className="space-y-3 rounded-card p-4 ring-1 ring-border-subtle ring-inset">
            <div className="grid items-start gap-3 md:grid-cols-[1fr_10rem_auto]">
              <Field label="Field name">{(id) => <Input id={id} disabled={ctx.readOnly} className="font-mono text-xs" value={part.name} maxLength={200} onChange={(e) => set(i, { ...part, name: e.target.value })} />}</Field>
              <Field label="Sends">
                {(id) => (
                  <Select
                    id={id}
                    disabled={ctx.readOnly}
                    value={isFilePart(part) ? 'file' : 'text'}
                    onChange={(e) => set(i, e.target.value === 'file' ? { name: part.name, file: '' } : { name: part.name, value: '' })}
                    options={[
                      { value: 'text', label: 'Text' },
                      { value: 'file', label: 'A file' },
                    ]}
                  />
                )}
              </Field>
              {!ctx.readOnly && parts.length > 1 ? <Button variant="ghost" className="mt-6" aria-label={`Remove field ${part.name}`} onClick={() => onChange(parts.filter((_, k) => k !== i))} icon={<Trash2 className="size-4" />} /> : <span />}
            </div>
            {isFilePart(part) ? (
              <>
                <FilePicker label="File" value={part.file} source={part.source ?? 'project'} ctx={ctx} onChange={(v, src) => set(i, { ...part, file: v, source: src === 'agent' ? 'agent' : undefined })} />
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="File name sent (optional)" hint="Default: the file’s own name">
                    {(id) => <Input id={id} disabled={ctx.readOnly} className="font-mono text-xs" value={part.filename ?? ''} maxLength={500} onChange={(e) => set(i, { ...part, filename: e.target.value || undefined })} />}
                  </Field>
                  <Field label="Content type (optional)" hint="Default: the file’s type (from its extension on the agent)">
                    {(id) => <Input id={id} disabled={ctx.readOnly} className="font-mono text-xs" value={part.contentType ?? ''} maxLength={200} placeholder="image/png" onChange={(e) => set(i, { ...part, contentType: e.target.value || undefined })} />}
                  </Field>
                </div>
              </>
            ) : (
              <TemplateField label="Value" place="text" multiline={part.value.includes('\n')} value={part.value} ctx={ctx} onChange={(v) => set(i, { ...part, value: v })} />
            )}
          </li>
        ))}
      </ol>
      {!ctx.readOnly && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => onChange([...parts, { name: `field${parts.length + 1}`, value: '' }])} icon={<Plus className="size-4" />}>
            Add a text field
          </Button>
          <Button variant="outline" size="sm" onClick={() => onChange([...parts, { name: parts.some(isFilePart) ? `file${parts.length + 1}` : 'file', file: '' }])} icon={<Paperclip className="size-4" />}>
            Add a file field
          </Button>
        </div>
      )}
    </div>
  );
}

/** The body after choosing another kind: text carries over between text kinds; an upload starts with one file field. */
function bodyOfKind(body: HttpStep['body'], kind: HttpStep['body']['kind']): HttpStep['body'] {
  if (kind === 'none') return { kind: 'none' };
  if (kind === 'multipart') return body.kind === 'multipart' ? body : { kind: 'multipart', parts: [{ name: 'file', file: body.kind === 'file' ? body.file : '' }] };
  if (kind === 'file') return { kind: 'file', file: body.kind === 'file' ? body.file : body.kind === 'multipart' ? (body.parts.find(isFilePart)?.file ?? '') : '' };
  const text = body.kind === 'json' || body.kind === 'form' || body.kind === 'text' ? body : null;
  return { kind, value: text?.value ?? '', ...(text?.contentType ? { contentType: text.contentType } : {}) };
}

/**
 * A text of a request that may hold {{templates}}: edit it, insert a value at the cursor, or select part of it and make
 * it test data (parameterization).
 */
function TemplateField({ label, value, onChange, place, multiline, ctx, hint }: { label: string; value: string; onChange: (v: string) => void; place: TextPlace; multiline?: boolean; ctx: FieldContext; hint?: string }) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const selection = () => {
    const el = ref.current;
    return el ? { start: el.selectionStart ?? value.length, end: el.selectionEnd ?? value.length } : { start: value.length, end: value.length };
  };
  const insert = (name: string) => {
    const { start, end } = selection();
    onChange(`${value.slice(0, start)}{{${name}${name.startsWith('$') ? '' : SUFFIX[place]}}}${value.slice(end)}`);
  };
  const parameterize = () => {
    const { start, end } = selection();
    const raw = value.slice(start, end);
    if (!raw.trim()) return setProblem('Select the part of the text to replace first (for example the user name in the body).');
    if (/\{\{|\}\}/.test(raw)) return setProblem('The selection holds a {{value}} already; select plain text only.');
    setProblem(null);
    ctx.parameterize({
      raw,
      value: decodeFor(raw, place),
      place,
      before: value.slice(0, start),
      replaceHere: (template) => onChange(`${value.slice(0, start)}${template}${value.slice(end)}`),
    });
  };
  const props = { ref, value, disabled: ctx.readOnly, spellCheck: false, className: 'font-mono text-xs', onChange: (e: { target: { value: string } }) => onChange(e.target.value) };
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="space-y-2">
          {multiline ? <Textarea id={id} rows={Math.min(14, Math.max(3, value.split('\n').length + 1))} {...props} /> : <Input id={id} {...props} />}
          {!ctx.readOnly && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={parameterize} icon={<TextSelect className="size-3.5" />}>
                Make selection test data
              </Button>
              <Select
                aria-label={`Insert a value into ${label}`}
                wrapperClassName="w-56"
                value=""
                onChange={(e) => e.target.value && insert(e.target.value)}
                options={[{ value: '', label: 'Insert a value…' }]}
                groups={ctx.insertable.map((g) => ({ label: g.group, options: g.names.map((n) => ({ value: n, label: `{{${n}}}` })) }))}
              />
            </div>
          )}
          {problem && <p className="text-xs text-warning">{problem}</p>}
        </div>
      )}
    </Field>
  );
}

/** The inline form that names the selected value: test data of the project, here or everywhere it appears. */
function ParameterizeForm({
  pending,
  script,
  testData,
  onDone,
  onCancel,
}: {
  pending: { raw: string; value: string; place: TextPlace; replaceHere: (template: string) => void; before: string };
  script: PerfScript;
  testData: TestDataKeys;
  onDone: (p: { script: PerfScript | null; key: string; template: string; save: NewTestData | null; count: number }) => void;
  onCancel: () => void;
}) {
  const [key, setKey] = useState(() => suggestKey(pending.before));
  const exists = testData.has(key);
  const everywhereCount = replaceValue(script, pending.value, key).count;
  const [everywhere, setEverywhere] = useState(everywhereCount > 1 && pending.value.length >= 3);
  const [save, setSave] = useState(true);
  const [secret, setSecret] = useState(false);
  const valid = VARIABLE_KEY_RE.test(key);
  const apply = () => {
    const replaced = everywhere ? replaceValue(script, pending.value, key) : null;
    onDone({ script: replaced?.script ?? null, key, template: `{{${key}${SUFFIX[pending.place]}}}`, save: save && !exists ? { key, value: pending.value, isSecret: secret } : null, count: replaced?.count ?? 1 });
  };
  return (
    <div role="dialog" aria-label="Make test data" className="space-y-3 rounded-card bg-primary-subtle/40 p-4 text-sm ring-1 ring-primary/40 ring-inset">
      <p className="text-fg">
        Replace <code className="rounded bg-surface px-1 font-mono text-xs">{pending.value.length > 80 ? `${pending.value.slice(0, 80)}…` : pending.value}</code> with test data, so every user can
        send their own value.
      </p>
      {(/^\d{1,12}$/.test(pending.value) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(pending.value) || /id$/i.test(key)) && (
        <p className="flex items-start gap-2 rounded-field bg-warning-subtle p-2.5 text-xs text-fg ring-1 ring-warning/40 ring-inset">
          <CircleAlert className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden />
          <span>
            This looks like an id the app gives out (a tour, an order, a user). If it belongs to the signed-in user, test data
            sends the same record for every user, and other users get 403 or 404. Take it from the earlier response that lists
            it instead (open that request › Correlation), or record the journey again: 1QA takes such ids from the user’s own
            list by itself.
          </span>
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Test data name" hint={!valid ? 'Letters, digits and _, starting with a letter' : exists ? 'Uses the project’s existing test data of this name' : undefined}>
          {(id) => <Input id={id} value={key} invalid={!valid} autoFocus onChange={(e) => setKey(e.target.value.trim())} />}
        </Field>
        <div className="space-y-2 pt-6">
          <Switch checked={everywhere} onChange={setEverywhere} label={`Everywhere it appears (${everywhereCount} place${everywhereCount === 1 ? '' : 's'})`} />
          {!exists && <Switch checked={save} onChange={setSave} label="Save the recorded value as this project’s test data" />}
          {!exists && save && <Switch checked={secret} onChange={setSecret} label="Secret (passwords, keys): never shown again" />}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={!valid} onClick={apply} icon={<KeyRound className="size-3.5" />}>
          Use {`{{${valid ? key : '…'}}}`}
        </Button>
      </div>
    </div>
  );
}

/** Values taken from a response (correlation): where from, how, and where later requests use them. */
function ExtractorList({
  extract,
  onChange,
  stepId,
  script,
  usedIn,
  onScript,
  readOnly,
}: {
  extract: Extractor[];
  onChange: (e: Extractor[]) => void;
  stepId: string;
  script: PerfScript;
  usedIn: (name: string) => number[];
  onScript: (s: PerfScript, note: string) => void;
  readOnly: boolean;
}) {
  const [recorded, setRecorded] = useState<Record<number, string>>({});
  const set = (i: number, patch: Partial<Extractor>) => onChange(extract.map((e, k) => (k === i ? { ...e, ...patch, origin: 'user' } : e)));
  const taken = new Set(script.transactions.flatMap((t) => t.steps).flatMap((s) => (s.kind === 'http' ? s.extract.map((e) => e.var) : [])));
  const freshName = () => {
    let n = 1;
    while (taken.has(`value${n}`)) n++;
    return `value${n}`;
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-fg-secondary">
        Values the server sends that later requests must send back (session tokens, ids, CSRF fields). Each is taken from this request’s response and used as{' '}
        <code className="font-mono text-xs">{'{{name}}'}</code> in the requests after it.
      </p>
      {extract.length === 0 && <p className="text-sm text-fg-muted">Nothing is taken from this response.</p>}
      {extract.map((e, i) => {
        const uses = usedIn(e.var);
        const nameOk = VAR_RE.test(e.var);
        return (
          <div key={i} className="space-y-3 rounded-card p-4 ring-1 ring-border-subtle ring-inset">
            <div className="flex flex-wrap items-center gap-2">
              <Link2 className="size-4 text-primary" aria-hidden />
              <span className="font-mono text-sm text-fg">{`{{${e.var}}}`}</span>
              <Badge tone={e.origin === 'user' ? 'neutral' : 'brand'}>{e.origin === 'user' ? 'Added or changed by you' : 'Found by 1QA'}</Badge>
              {uses.length ? (
                <span className="text-xs text-fg-muted">Used in request{uses.length === 1 ? '' : 's'} {uses.join(', ')}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-warning">
                  <CircleAlert className="size-3.5" /> Not used by a later request yet
                </span>
              )}
              {!readOnly && <Button variant="ghost" size="sm" className="ml-auto" aria-label={`Remove ${e.var}`} onClick={() => onChange(extract.filter((_, k) => k !== i))} icon={<Trash2 className="size-4" />} />}
            </div>
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_2fr]">
              <Field label="Name" hint={nameOk ? undefined : 'Letters, digits and _'}>
                {(id) => (
                  <Input
                    id={id}
                    disabled={readOnly}
                    invalid={!nameOk}
                    defaultValue={e.var}
                    // Renamed on leaving the field, with every {{use}} of it.
                    onBlur={(ev) => {
                      const name = ev.target.value.trim();
                      if (name === e.var || !VAR_RE.test(name)) return;
                      const renamed = renameTemplate(script, e.var, name);
                      onScript(
                        { ...renamed, transactions: renamed.transactions.map((t) => ({ ...t, steps: t.steps.map((s) => (s.id === stepId && s.kind === 'http' ? { ...s, extract: s.extract.map((x, k) => (k === i ? { ...x, var: name, origin: 'user' as const } : x)) } : s)) })) },
                        `Renamed to {{${name}}} everywhere`,
                      );
                    }}
                  />
                )}
              </Field>
              <Field label="Take it from">
                {(id) => <Select id={id} disabled={readOnly} value={e.from} onChange={(ev) => set(i, { from: ev.target.value as Extractor['from'] })} options={Object.entries(FROM_LABEL).map(([v, x]) => ({ value: v, label: x.label }))} />}
              </Field>
              <Field label="Where" hint={FROM_LABEL[e.from].hint}>
                {(id) => <Input id={id} disabled={readOnly} className="font-mono text-xs" placeholder={FROM_LABEL[e.from].placeholder} value={e.expr} onChange={(ev) => set(i, { expr: ev.target.value })} />}
              </Field>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="When there are several">
                {(id) => (
                  <Select
                    id={id}
                    disabled={readOnly}
                    value={e.pick}
                    onChange={(ev) => set(i, { pick: ev.target.value as Extractor['pick'] })}
                    options={[
                      { value: 'first', label: 'Take the first' },
                      { value: 'random', label: 'Take one at random' },
                      { value: 'all', label: 'Take all (a list)' },
                    ]}
                  />
                )}
              </Field>
              <Field label="Decode">
                {(id) => (
                  <Select
                    id={id}
                    disabled={readOnly}
                    value={e.decode ?? ''}
                    onChange={(ev) => set(i, { decode: (ev.target.value || undefined) as Extractor['decode'] })}
                    options={[
                      { value: '', label: 'As it is' },
                      { value: 'url', label: 'URL-decode (%2F → /)' },
                      { value: 'html', label: 'HTML-decode (&amp;amp; → &amp;)' },
                    ]}
                  />
                )}
              </Field>
              <div className="pt-6">
                <Switch checked={e.required} disabled={readOnly} onChange={(v) => set(i, { required: v })} label="Fail the request when it is not found" />
              </div>
            </div>
            {!readOnly && (
              <div className="flex flex-wrap items-end gap-2">
                <Field label="Value it had when recorded" hint="Paste it to replace it with this name in every later request" className="min-w-0 flex-1">
                  {(id) => <Input id={id} className="font-mono text-xs" value={recorded[i] ?? ''} onChange={(ev) => setRecorded({ ...recorded, [i]: ev.target.value })} />}
                </Field>
                <Button
                  variant="outline"
                  disabled={!recorded[i]?.trim() || !nameOk}
                  onClick={() => {
                    const r = replaceValue(script, recorded[i]!.trim(), e.var, stepId);
                    onScript(r.script, r.count ? `{{${e.var}}} now used in ${r.count} place${r.count === 1 ? '' : 's'} after this request` : 'That value is not in any later request');
                    if (r.count) setRecorded({ ...recorded, [i]: '' });
                  }}
                  icon={<Wand2 className="size-4" />}
                >
                  Use in later requests
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <Button variant="outline" size="sm" onClick={() => onChange([...extract, { var: freshName(), from: 'json', expr: '$.', pick: 'first', origin: 'user', required: true }])} icon={<Plus className="size-4" />}>
          Take a value from this response
        </Button>
      )}
    </div>
  );
}

/** What 1QA checks on the response; a failed check counts as an error of the request. */
function CheckList({ checks, onChange, readOnly }: { checks: Check[]; onChange: (c: Check[]) => void; readOnly: boolean }) {
  const set = (i: number, c: Check) => onChange(checks.map((x, k) => (k === i ? c : x)));
  return (
    <div className="space-y-3">
      {checks.length === 0 && <p className="text-sm text-fg-muted">No checks: any answer counts as success unless the status is an error.</p>}
      {checks.map((c, i) => (
        <div key={i} className="grid items-end gap-3 sm:grid-cols-[1fr_2fr_auto]">
          <Field label="Check">
            {(id) => (
              <Select
                id={id}
                disabled={readOnly}
                value={c.kind}
                onChange={(e) => set(i, { kind: e.target.value as Check['kind'], expect: e.target.value === 'status' ? 200 : '' })}
                options={[
                  { value: 'status', label: 'Status is' },
                  { value: 'text', label: 'Response contains' },
                  { value: 'notText', label: 'Response does not contain' },
                ]}
              />
            )}
          </Field>
          <Field label={c.kind === 'status' ? 'Status code' : 'Text'}>
            {(id) =>
              c.kind === 'status' ? (
                <Input id={id} type="number" min={100} max={599} disabled={readOnly} value={c.expect} onChange={(e) => set(i, { ...c, expect: Math.round(Number(e.target.value)) })} />
              ) : (
                <Input id={id} disabled={readOnly} value={String(c.expect)} onChange={(e) => set(i, { ...c, expect: e.target.value })} />
              )
            }
          </Field>
          {!readOnly ? <Button variant="ghost" aria-label="Remove check" onClick={() => onChange(checks.filter((_, k) => k !== i))} icon={<Trash2 className="size-4" />} /> : <span />}
        </div>
      ))}
      {!readOnly && (
        <Button variant="outline" size="sm" onClick={() => onChange([...checks, { kind: 'text', expect: '' }])} icon={<Plus className="size-4" />}>
          Add a check
        </Button>
      )}
    </div>
  );
}

type StepTab = 'request' | 'headers' | 'body' | 'correlation' | 'checks' | 'messages';

/** One request opened for editing, tab by tab. */
function StepEditor({
  step,
  onStep,
  script,
  onScript,
  ctx,
  usedIn,
}: {
  step: Step;
  onStep: (s: Step) => void;
  script: PerfScript;
  onScript: (s: PerfScript, note: string) => void;
  ctx: FieldContext;
  usedIn: (name: string) => number[];
}) {
  const [tab, setTab] = useState<StepTab>('request');
  const readOnly = ctx.readOnly;
  const headers = (
    <div className="space-y-3">
      {step.headers.length === 0 && <p className="text-sm text-fg-muted">No headers of its own (the script’s default headers are sent).</p>}
      {step.headers.map((h, i) => (
        <div key={i} className="grid items-start gap-3 md:grid-cols-[1fr_2fr_auto]">
          <Field label="Header">{(id) => <Input id={id} disabled={readOnly} className="font-mono text-xs" value={h.name} onChange={(e) => onStep({ ...step, headers: step.headers.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)) } as Step)} />}</Field>
          <TemplateField label="Value" place="header" value={h.value} ctx={ctx} onChange={(v) => onStep({ ...step, headers: step.headers.map((x, k) => (k === i ? { ...x, value: v } : x)) } as Step)} />
          {!readOnly ? <Button variant="ghost" className="mt-6" aria-label={`Remove header ${h.name}`} onClick={() => onStep({ ...step, headers: step.headers.filter((_, k) => k !== i) } as Step)} icon={<Trash2 className="size-4" />} /> : <span />}
        </div>
      ))}
      {!readOnly && (
        <Button variant="outline" size="sm" onClick={() => onStep({ ...step, headers: [...step.headers, { name: '', value: '' }] } as Step)} icon={<Plus className="size-4" />}>
          Add a header
        </Button>
      )}
    </div>
  );

  if (step.kind === 'ws') {
    return (
      <div className="space-y-4">
        <Tabs<StepTab>
          label="WebSocket parts"
          value={tab === 'request' || tab === 'headers' || tab === 'messages' ? tab : 'request'}
          onChange={setTab}
          items={[
            { id: 'request', label: 'Connection' },
            { id: 'headers', label: 'Headers', count: step.headers.length },
            { id: 'messages', label: 'Messages', count: step.actions.length },
          ]}
        />
        {tab === 'headers' ? (
          <TabPanel id="headers">{headers}</TabPanel>
        ) : tab === 'messages' ? (
          <TabPanel id="messages">
            <ol className="space-y-3">
              {step.actions.map((a, i) => {
                const setAction = (next: typeof a) => onStep({ ...step, actions: step.actions.map((x, k) => (k === i ? next : x)) });
                return (
                  <li key={i} className="space-y-2 rounded-card p-3 ring-1 ring-border-subtle ring-inset">
                    {a.op === 'send' ? (
                      <TemplateField label={`${i + 1}. Send`} place="text" multiline value={a.message} ctx={ctx} onChange={(v) => setAction({ ...a, message: v })} />
                    ) : a.op === 'expect' ? (
                      <>
                        <Field label={`${i + 1}. Wait for a message containing`}>{(id) => <Input id={id} disabled={readOnly} className="font-mono text-xs" value={a.match} onChange={(e) => setAction({ ...a, match: e.target.value })} />}</Field>
                        <ExtractorList extract={a.extract} onChange={(x) => setAction({ ...a, extract: x })} stepId={step.id} script={script} usedIn={usedIn} onScript={onScript} readOnly={readOnly} />
                      </>
                    ) : (
                      <p className="text-sm text-fg-secondary">{i + 1}. Close the connection</p>
                    )}
                  </li>
                );
              })}
            </ol>
          </TabPanel>
        ) : (
          <TabPanel id="request">
            <div className="space-y-4">
              <Field label="Name in the results">{(id) => <Input id={id} disabled={readOnly} value={step.name} maxLength={200} onChange={(e) => onStep({ ...step, name: e.target.value })} />}</Field>
              <TemplateField label="Address" place="url" value={step.url} ctx={ctx} onChange={(v) => onStep({ ...step, url: v })} />
            </div>
          </TabPanel>
        )}
      </div>
    );
  }

  const body = step.body;
  return (
    <div className="space-y-4">
      <Tabs<StepTab>
        label="Request parts"
        value={tab}
        onChange={setTab}
        items={[
          { id: 'request', label: 'Request' },
          { id: 'headers', label: 'Headers', count: step.headers.length },
          { id: 'body', label: 'Body' },
          { id: 'correlation', label: 'Correlation', count: step.extract.length },
          { id: 'checks', label: 'Checks', count: step.checks.length },
        ]}
      />
      {tab === 'request' && (
        <TabPanel id="request">
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[8rem_1fr]">
              <Field label="Method">{(id) => <Select id={id} disabled={readOnly} value={step.method} onChange={(e) => onStep({ ...step, method: e.target.value })} options={METHODS.map((m) => ({ value: m, label: m }))} />}</Field>
              <Field label="Name in the results" hint="Keep ids out of it ({cartId}), so results group the same request together">
                {(id) => <Input id={id} disabled={readOnly} value={step.name} maxLength={200} onChange={(e) => onStep({ ...step, name: e.target.value })} />}
              </Field>
            </div>
            <TemplateField label="Address" place="url" value={step.url} ctx={ctx} hint="Absolute, or a path after the app’s address" onChange={(v) => onStep({ ...step, url: v })} />
          </div>
        </TabPanel>
      )}
      {tab === 'headers' && <TabPanel id="headers">{headers}</TabPanel>}
      {tab === 'body' && (
        <TabPanel id="body">
          <div className="space-y-4">
            <Field label="Body">
              {(id) => (
                <Select
                  id={id}
                  disabled={readOnly}
                  wrapperClassName="max-w-xs"
                  value={body.kind}
                  onChange={(e) => onStep({ ...step, body: bodyOfKind(body, e.target.value as HttpStep['body']['kind']) })}
                  options={[
                    { value: 'none', label: 'No body' },
                    { value: 'json', label: 'JSON' },
                    { value: 'form', label: 'Form (a=1&b=2)' },
                    { value: 'multipart', label: 'Upload form (files and fields)' },
                    { value: 'file', label: 'A file (the whole body)' },
                    { value: 'text', label: 'Other text' },
                  ]}
                />
              )}
            </Field>
            {body.kind === 'multipart' && <MultipartFields parts={body.parts} ctx={ctx} onChange={(parts) => onStep({ ...step, body: { ...body, parts } })} />}
            {body.kind === 'file' && (
              <>
                <FilePicker label="File" value={body.file} source={body.source ?? 'project'} ctx={ctx} onChange={(v, src) => onStep({ ...step, body: { ...body, file: v, source: src === 'agent' ? 'agent' : undefined } })} />
                <Field label="Content type (optional)" hint="Default: the test file’s type (for example image/png)">
                  {(id) => <Input id={id} disabled={readOnly} className="max-w-xs font-mono text-xs" value={body.contentType ?? ''} maxLength={200} onChange={(e) => onStep({ ...step, body: { ...body, contentType: e.target.value || undefined } })} />}
                </Field>
              </>
            )}
            {(body.kind === 'json' || body.kind === 'form' || body.kind === 'text') && (
              <>
                <TemplateField label="Content" place={body.kind} multiline value={body.value} ctx={ctx} onChange={(v) => onStep({ ...step, body: { ...body, value: v } })} />
                {body.kind === 'json' && !readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      try {
                        onStep({ ...step, body: { ...body, value: JSON.stringify(JSON.parse(body.value), null, 2) } });
                      } catch {
                        /* not plain JSON (a {{value}} outside quotes): left as it is */
                      }
                    }}
                  >
                    Format the JSON
                  </Button>
                )}
              </>
            )}
          </div>
        </TabPanel>
      )}
      {tab === 'correlation' && (
        <TabPanel id="correlation">
          <ExtractorList extract={step.extract} onChange={(x) => onStep({ ...step, extract: x })} stepId={step.id} script={script} usedIn={usedIn} onScript={onScript} readOnly={readOnly} />
        </TabPanel>
      )}
      {tab === 'checks' && (
        <TabPanel id="checks">
          <CheckList checks={step.checks} onChange={(c) => onStep({ ...step, checks: c })} readOnly={readOnly} />
        </TabPanel>
      )}
    </div>
  );
}

/** Seconds field for a pause, stored in milliseconds. */
function SecondsInput({ label, ms, onChange, disabled }: { label: string; ms: number; onChange: (ms: number) => void; disabled: boolean }) {
  return <Input aria-label={label} type="number" min={0} step={0.5} disabled={disabled} className="w-20" value={Math.round(ms / 100) / 10} onChange={(e) => onChange(Math.max(0, Math.round(Number(e.target.value) * 1000)))} />;
}

/**
 * A performance script, request by request: transactions (one per recorded click) with their requests in order. Opening
 * a request edits its address, headers, body, correlation (values taken from its response for later requests) and
 * checks. Selected text becomes test data (parameterization); every value the script uses is listed with where it comes
 * from, and problems (a value used before a response gives it, test data the project lacks) are shown.
 */
export function ScriptEditor({
  projectId,
  script,
  onChange,
  readOnly,
  testData,
  onNewTestData,
  notify,
}: {
  projectId: string;
  script: PerfScript;
  onChange: (s: PerfScript) => void;
  readOnly: boolean;
  testData: TestDataKeys;
  /** Saves values made test data from a selection to the project (when the person asked to). */
  onNewTestData: (v: NewTestData) => void;
  notify: (message: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [pending, setPending] = useState<{ raw: string; value: string; place: TextPlace; replaceHere: (template: string) => void; before: string; stepId: string } | null>(null);
  const vars = scriptVariables(script);
  const steps = script.transactions.flatMap((t) => t.steps);
  const number = new Map(steps.map((s, i) => [s.id, i + 1]));
  const usedIn = (name: string) => steps.filter((s) => vars.uses.get(s.id)?.includes(name)).map((s) => number.get(s.id)!);
  const tooEarly = new Map(vars.usedTooEarly.map((u) => [u.stepId, u.name]));
  const dataNames = vars.data.filter((n) => n !== 'APP_URL');
  const missingData = dataNames.filter((n) => !testData.has(n));
  const frontEnd = frontEndSteps(script);
  // Upload fields: files the project has not uploaded yet, and fields without a file.
  const files = usePerfFiles(projectId);
  const fileNames = new Set((files.data ?? []).map((f) => f.name.toLowerCase()));
  const missingFiles = files.data ? fixedFileNames(script).filter((n) => !fileNames.has(n.toLowerCase())) : [];
  const unchosenFiles = scriptFileUses(script).filter((u) => !u.ref.trim()).map((u) => number.get(u.stepId)!);
  const removeFrontEnd = () => {
    if (frontEnd.length >= steps.length) return notify('Every request looks like a front-end request: nothing was removed.');
    const drop = new Set(frontEnd);
    onChange({ ...script, transactions: script.transactions.map((t) => ({ ...t, steps: t.steps.filter((s) => !drop.has(s.id)) })).filter((t) => t.steps.length > 0) });
    setOpen(null);
    notify(`${drop.size} front-end request${drop.size === 1 ? '' : 's'} removed. Save to keep the change.`);
  };

  const updateStep = (id: string, next: Step) => onChange({ ...script, transactions: script.transactions.map((t) => ({ ...t, steps: t.steps.map((s) => (s.id === id ? next : s)) })) });
  const removeStep = (id: string) => {
    if (steps.length <= 1) return notify('A script needs at least one request.');
    onChange({ ...script, transactions: script.transactions.map((t) => ({ ...t, steps: t.steps.filter((s) => s.id !== id) })).filter((t) => t.steps.length > 0) });
    setOpen(null);
  };
  const stepTakesOf = (st: Step) => (st.kind === 'http' ? st.extract.map((e) => e.var) : st.actions.flatMap((a) => (a.op === 'expect' ? a.extract.map((e) => e.var) : [])));
  // Skipped requests stay in the script (turn them back on any time) but are not sent in checks or runs.
  const toggleSkip = (st: Step) => {
    const skip = !st.skip;
    if (skip && steps.filter((s) => !s.skip).length <= 1) return notify('A script needs at least one request that is sent.');
    updateStep(st.id, { ...st, skip: skip || undefined } as Step);
    const takes = stepTakesOf(st);
    const later = steps.slice(number.get(st.id)!).filter((s) => !s.skip && takes.some((v) => vars.uses.get(s.id)?.includes(v)));
    if (skip && later.length) notify(`Skipped. It takes ${takes.join(', ')}, which request${later.length === 1 ? '' : 's'} ${later.map((s) => number.get(s.id)).join(', ')} use${later.length === 1 ? 's' : ''}: they will get no value unless an earlier request takes it.`);
    else notify(skip ? 'Skipped: it stays in the script but is not sent. Save to keep the change.' : 'Turned back on. Save to keep the change.');
  };
  const moveStep = (ti: number, si: number, by: -1 | 1) => {
    const t = script.transactions[ti]!;
    const to = si + by;
    if (to < 0 || to >= t.steps.length) return;
    const next = [...t.steps];
    [next[si], next[to]] = [next[to]!, next[si]!];
    onChange({ ...script, transactions: script.transactions.map((x, k) => (k === ti ? { ...x, steps: next } : x)) });
  };
  const setTx = (ti: number, patch: Partial<PerfScript['transactions'][number]>) => onChange({ ...script, transactions: script.transactions.map((x, k) => (k === ti ? { ...x, ...patch } : x)) });

  const ctxFor = (stepId: string): FieldContext => {
    const before = steps.slice(0, number.get(stepId)! - 1);
    const earlier = [...new Set(before.flatMap((s) => (s.kind === 'http' ? s.extract.map((e) => e.var) : s.actions.flatMap((a) => (a.op === 'expect' ? a.extract.map((e) => e.var) : [])))))];
    const data = [...new Set([...testData.keys(), ...dataNames])].sort();
    return {
      readOnly,
      insertable: [
        { group: 'From earlier responses', names: earlier },
        { group: 'Test data', names: data },
        { group: 'Per user, each journey', names: script.iterationVars.map((v) => v.name) },
        { group: 'Made up each time', names: GENERATORS },
      ].filter((g) => g.names.length),
      parameterize: (p) => setPending({ ...p, stepId }),
      files: files.data ?? [],
    };
  };

  return (
    <div className="space-y-6">
      <section aria-labelledby="script-values" className="space-y-3 rounded-card bg-surface-sunken p-4 ring-1 ring-border-subtle ring-inset">
        <h3 id="script-values" className="text-xs font-semibold tracking-label text-fg-muted uppercase">
          Values the script uses
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1 text-fg-secondary">
            <KeyRound className="size-4 text-primary" aria-hidden /> Test data:
          </span>
          {dataNames.length === 0 && <span className="text-fg-muted">none yet - select a value in a request and make it test data</span>}
          {dataNames.map((n) => (
            <Badge key={n} tone={!testData.has(n) ? 'danger' : testData.get(n) ? 'warning' : 'neutral'}>
              {n}
              {testData.get(n) ? ' (secret)' : !testData.has(n) ? ' (missing)' : ''}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1 text-fg-secondary">
            <Link2 className="size-4 text-primary" aria-hidden /> From responses:
          </span>
          {vars.extracted.size === 0 && <span className="text-fg-muted">none</span>}
          {[...vars.extracted].map(([name, stepId]) => {
            const uses = usedIn(name);
            return (
              <button key={name} type="button" onClick={() => setOpen(stepId)} className="rounded-full px-2.5 py-0.5 text-xs ring-1 ring-border ring-inset hover:ring-primary" title="Open the request that takes it">
                <span className="font-mono text-fg">{name}</span>
                <span className="text-fg-muted">
                  {' '}
                  ← request {number.get(stepId)} · {uses.length ? `used in ${uses.join(', ')}` : 'not used'}
                </span>
              </button>
            );
          })}
        </div>
        {missingData.length > 0 && (
          <p className="flex items-start gap-2 text-sm text-fg">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-danger-text" />
            <span>
              Runs need test data the project does not have: {missingData.join(', ')}. Add it on the{' '}
              <Link to={`/projects/${projectId}?tab=data`} className="text-link hover:underline">
                Test data
              </Link>{' '}
              tab.
            </span>
          </p>
        )}
        {(missingFiles.length > 0 || unchosenFiles.length > 0) && (
          <p className="flex items-start gap-2 text-sm text-fg">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-danger-text" />
            <span>
              {missingFiles.length > 0 && <>The script uploads {missingFiles.join(', ')}, which {missingFiles.length === 1 ? 'is' : 'are'} not one of the project’s test files yet: upload {missingFiles.length === 1 ? 'it' : 'them'} on the{' '}
                <Link to={`/projects/${projectId}?tab=scripts`} className="text-link hover:underline">
                  Scripts
                </Link>{' '}
                tab (Test files). </>}
              {unchosenFiles.length > 0 && `Request${unchosenFiles.length === 1 ? '' : 's'} ${[...new Set(unchosenFiles)].join(', ')} ${unchosenFiles.length === 1 ? 'has' : 'have'} an upload field without a file: open ${unchosenFiles.length === 1 ? 'it' : 'them'} and choose one.`}
            </span>
          </p>
        )}
        {frontEnd.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-fg">
            <CircleAlert className="size-4 shrink-0 text-warning" />
            <span className="min-w-0 flex-1">
              {frontEnd.length} request{frontEnd.length === 1 ? '' : 's'} ({frontEnd.map((id) => number.get(id)).join(', ')}) {frontEnd.length === 1 ? 'is' : 'are'} the browser’s front end
              (pages, Next.js React Server Component requests, prefetches), not backend calls. They usually fail under load, because a load test runs no browser
              JavaScript.
            </span>
            {!readOnly && (
              <Button variant="outline" size="sm" onClick={removeFrontEnd} icon={<Trash2 className="size-4" />}>
                Remove front-end requests
              </Button>
            )}
          </div>
        )}
        {vars.usedTooEarly.map((u) => (
          <p key={`${u.stepId}-${u.name}`} className="flex items-start gap-2 text-sm text-fg">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
            Request {number.get(u.stepId)} uses {`{{${u.name}}}`} before request {number.get(vars.extracted.get(u.name)!)} takes it from its response: move the request, or take the value
            earlier.
          </p>
        ))}
      </section>

      <ol className="space-y-4">
        {script.transactions.map((t, ti) => (
          <li key={t.id} className="rounded-card ring-1 ring-border-subtle ring-inset">
            <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-4 py-3">
              <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary-subtle text-xs font-semibold text-primary tabular-nums">{ti + 1}</span>
              {readOnly ? (
                <span className="min-w-0 flex-1 font-medium text-fg">{t.name}</span>
              ) : (
                <Input aria-label={`Transaction ${ti + 1} name`} className="min-w-0 flex-1 font-medium" value={t.name} maxLength={120} onChange={(e) => setTx(ti, { name: e.target.value })} />
              )}
              <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
                <Timer className="size-3.5" aria-hidden /> Pause after
                {t.thinkTimeMs ? (
                  <>
                    <SecondsInput label="Shortest pause (s)" disabled={readOnly} ms={t.thinkTimeMs.min} onChange={(ms) => setTx(ti, { thinkTimeMs: { min: ms, max: Math.max(ms, t.thinkTimeMs!.max) } })} />–
                    <SecondsInput label="Longest pause (s)" disabled={readOnly} ms={t.thinkTimeMs.max} onChange={(ms) => setTx(ti, { thinkTimeMs: { min: Math.min(ms, t.thinkTimeMs!.min), max: ms } })} />s
                  </>
                ) : readOnly ? (
                  'none'
                ) : (
                  <button type="button" className="text-link hover:underline" onClick={() => setTx(ti, { thinkTimeMs: { min: 1000, max: 3000 } })}>
                    none - add one
                  </button>
                )}
              </span>
            </div>
            <ol className="divide-y divide-border-subtle">
              {t.steps.map((st, si) => {
                const n = number.get(st.id)!;
                const isOpen = open === st.id;
                const takes = st.kind === 'http' ? st.extract.map((e) => e.var) : [];
                const uses = [...new Set((vars.uses.get(st.id) ?? []).filter((u) => !u.startsWith('$')))];
                return (
                  <li key={st.id} className={clsx(isOpen && 'bg-surface-sunken/60', st.skip && 'opacity-60')} data-skipped={st.skip ? 'true' : undefined}>
                    <div className="flex items-center gap-2 px-4 py-2.5">
                      <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : st.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                        {isOpen ? <ChevronDown className="size-3.5 shrink-0 text-fg-muted" /> : <ChevronRight className="size-3.5 shrink-0 text-fg-muted" />}
                        <span className="w-6 shrink-0 text-right text-xs text-fg-muted tabular-nums">{n}</span>
                        <Badge tone={st.kind === 'ws' ? 'brand' : (METHOD_TONE[st.method] ?? 'neutral')}>{st.kind === 'ws' ? 'WS' : st.method}</Badge>
                        {st.skip && <Badge tone="muted">Skipped</Badge>}
                        <span className="min-w-0 flex-1">
                          <TemplateText text={st.url} className={clsx('block truncate text-xs text-fg', st.skip && 'line-through')} />
                          {(takes.length > 0 || uses.length > 0 || tooEarly.has(st.id)) && (
                            <span className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-fg-muted">
                              {takes.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-primary">
                                  <Link2 className="size-3" aria-hidden /> takes {takes.join(', ')}
                                </span>
                              )}
                              {uses.length > 0 && <span>uses {uses.join(', ')}</span>}
                              {tooEarly.has(st.id) && (
                                <span className="inline-flex items-center gap-1 text-warning">
                                  <CircleAlert className="size-3" aria-hidden /> {tooEarly.get(st.id)} not taken yet
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </button>
                      {!readOnly && (isOpen || st.skip) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-pressed={Boolean(st.skip)}
                          title={st.skip ? 'Send this request again' : 'Keep it in the script but do not send it'}
                          onClick={() => toggleSkip(st)}
                          icon={<Ban className="size-3.5" />}
                        >
                          {st.skip ? 'Turn on' : 'Skip'}
                        </Button>
                      )}
                      {!readOnly && isOpen && (
                        <span className="flex shrink-0 gap-1">
                          <Button variant="ghost" size="sm" aria-label="Move up" disabled={si === 0} onClick={() => moveStep(ti, si, -1)} icon={<ArrowUp className="size-3.5" />} />
                          <Button variant="ghost" size="sm" aria-label="Move down" disabled={si === t.steps.length - 1} onClick={() => moveStep(ti, si, 1)} icon={<ArrowDown className="size-3.5" />} />
                          <Button variant="ghost" size="sm" aria-label={`Remove request ${n}`} onClick={() => removeStep(st.id)} icon={<Trash2 className="size-3.5" />} />
                        </span>
                      )}
                    </div>
                    {isOpen && (
                      <div className="space-y-4 px-4 pt-1 pb-5 md:pl-14">
                        {pending?.stepId === st.id && (
                          <ParameterizeForm
                            pending={pending}
                            script={script}
                            testData={testData}
                            onCancel={() => setPending(null)}
                            onDone={(r) => {
                              if (r.script) onChange(r.script);
                              else pending.replaceHere(r.template);
                              if (r.save) onNewTestData(r.save);
                              notify(`{{${r.key}}} used in ${r.count} place${r.count === 1 ? '' : 's'}`);
                              setPending(null);
                            }}
                          />
                        )}
                        <StepEditor
                          key={st.id}
                          step={st}
                          onStep={(s) => updateStep(st.id, s)}
                          script={script}
                          onScript={(s, note) => (onChange(s), notify(note))}
                          ctx={ctxFor(st.id)}
                          usedIn={usedIn}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </li>
        ))}
      </ol>
    </div>
  );
}
